import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { focusRing, humanize } from './outreachUtils';

export function PageHeader({ title, description, refreshing, onRefresh }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        {description ? <p className="mt-1 text-sm text-gray-600">{description}</p> : null}
      </div>
      {onRefresh ? (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className={`inline-flex min-h-[44px] items-center justify-center gap-2 self-start rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`}
        >
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      ) : null}
    </div>
  );
}

export function PageState({
  loading,
  error,
  permissionDenied,
  empty,
  emptyTitle = 'Nothing to show yet',
  emptyMessage = 'Data will appear here when it becomes available.',
  onRetry,
  children,
}) {
  if (loading) {
    return (
      <div aria-live="polite" aria-busy="true" className="space-y-3">
        {[1, 2, 3].map((item) => (
          <div key={item} className="h-20 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    );
  }

  if (error) {
    const Icon = permissionDenied ? LockClosedIcon : ExclamationTriangleIcon;
    return (
      <div
        role="alert"
        className={`rounded-xl border p-6 ${
          permissionDenied
            ? 'border-amber-200 bg-amber-50 text-amber-900'
            : 'border-red-200 bg-red-50 text-red-900'
        }`}
      >
        <div className="flex gap-3">
          <Icon className="mt-0.5 h-6 w-6 flex-none" />
          <div>
            <p className="font-semibold">
              {permissionDenied ? 'Founder access required' : 'Outreach data unavailable'}
            </p>
            <p className="mt-1 text-sm">{error}</p>
            {onRetry && !permissionDenied ? (
              <button
                type="button"
                onClick={onRetry}
                className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
              >
                Try again
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (empty) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
        <InformationCircleIcon className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-3 font-semibold text-gray-800">{emptyTitle}</p>
        <p className="mt-1 text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return children;
}

export function StaleNotice({ generatedAt, loadedAt }) {
  const timestamp = generatedAt ? new Date(generatedAt) : loadedAt;
  if (!timestamp || Number.isNaN(timestamp.getTime())) return null;
  const stale = Date.now() - timestamp.getTime() > 15 * 60 * 1000;

  return (
    <p
      className={`text-xs ${stale ? 'font-medium text-amber-700' : 'text-gray-500'}`}
      aria-live="polite"
    >
      {stale ? 'Stale data · ' : 'Updated '}
      {timestamp.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}{' '}
      CT
    </p>
  );
}

export function StatusBadge({ value = 'unknown' }) {
  const normalized = String(value || 'unknown').toLowerCase();
  const colors = {
    active: 'border-green-200 bg-green-50 text-green-800',
    healthy: 'border-green-200 bg-green-50 text-green-800',
    sent: 'border-green-200 bg-green-50 text-green-800',
    delivered: 'border-green-200 bg-green-50 text-green-800',
    approved: 'border-green-200 bg-green-50 text-green-800',
    on: 'border-green-200 bg-green-50 text-green-800',
    shadow: 'border-blue-200 bg-blue-50 text-blue-800',
    queued: 'border-blue-200 bg-blue-50 text-blue-800',
    pending: 'border-blue-200 bg-blue-50 text-blue-800',
    syncing: 'border-blue-200 bg-blue-50 text-blue-800',
    paused: 'border-amber-200 bg-amber-50 text-amber-800',
    deferred: 'border-amber-200 bg-amber-50 text-amber-800',
    suppressed: 'border-amber-200 bg-amber-50 text-amber-800',
    off: 'border-gray-200 bg-gray-100 text-gray-700',
    cancelled: 'border-gray-200 bg-gray-100 text-gray-700',
    skipped: 'border-gray-200 bg-gray-100 text-gray-700',
    failed: 'border-red-200 bg-red-50 text-red-800',
    blocked: 'border-red-200 bg-red-50 text-red-800',
    unknown: 'border-purple-200 bg-purple-50 text-purple-800',
  };

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
        colors[normalized] || colors.unknown
      }`}
    >
      {humanize(normalized)}
    </span>
  );
}

export function StatCard({ label, value, detail, tone = 'default' }) {
  const toneClass = {
    default: 'border-gray-200 bg-white',
    positive: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    danger: 'border-red-200 bg-red-50',
  }[tone];

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value ?? '—'}</p>
      {detail ? <p className="mt-1 text-xs text-gray-600">{detail}</p> : null}
    </div>
  );
}

export function Modal({
  title,
  description,
  children,
  confirmLabel,
  confirmDisabled = false,
  busy,
  onConfirm,
  onClose,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 p-4"
      role="presentation"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) onClose();
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="outreach-dialog-title"
        className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl"
      >
        <h3 id="outreach-dialog-title" className="text-lg font-semibold text-gray-900">
          {title}
        </h3>
        {description ? <p className="mt-2 text-sm text-gray-600">{description}</p> : null}
        <div className="mt-5">{children}</div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            autoFocus
            onClick={onClose}
            disabled={busy}
            className={`min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 ${focusRing}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            className={`min-h-[44px] rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`}
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

export function ReasonList({ reasons, emptyLabel = 'No blocking reasons' }) {
  const rows = Array.isArray(reasons) ? reasons : reasons ? [reasons] : [];
  if (rows.length === 0) return <span className="text-sm text-gray-500">{emptyLabel}</span>;
  return (
    <ul className="space-y-1 text-sm text-red-700">
      {rows.map((reason, index) => {
        const label =
          typeof reason === 'string'
            ? humanize(reason)
            : reason.message || humanize(reason.code || reason.reason);
        return <li key={`${label}-${index}`}>• {label}</li>;
      })}
    </ul>
  );
}
